import * as R from 'ramda'
import { SemanticICONS } from 'semantic-ui-react'
import React from 'react'

export type Filter = {
  value: string | number | boolean
  label?: string
}

export type Comparison =
  | 'is'
  | 'is_not'
  | 'contains'
  | 'does_not_contains'
  | 'is_unknown'
  | 'has_any_value'

export type InputProps<T> = {
  onChange: (filters: Filter[]) => void
  defaultFilters: Filter[]
  onClose?: () => void
  rule?: Rule<T>
}

export type Comparisons = {
  label: string
  value: string
  inputComponent: ((props: InputProps<any>) => JSX.Element) | null
}

export type Rule<T> = {
  field?: string
  label?: string
  icon?: SemanticICONS
  comparison?: string
  filters?: Filter[]
  inputComponent?: React.FunctionComponent<InputProps<T>> | React.ComponentClass<InputProps<T>>
  extra?: T
}

export type Group<T> = {
  operator: string
  rules: (Rule<T> | undefined)[]
}

type Action<T> = {
  type:
    | 'CLICK_ADD_GROUP'
    | 'CLICK_ADD_RULE'
    | 'CLOSE_DROPDOWN'
    | 'ADD_GROUP'
    | 'ADD_RULE'
    | 'SET_FILTER'
    | 'CLOSE_POPOVER'
    | 'OPEN_POPOVER'
    | 'REMOVE_RULE'
    | 'SET_COMPARISON'
    | 'SET_EXTRA'
    | 'SET_GLOBAL_OPERATOR'
    | 'SET_GROUP_OPERATOR'
  filters?: Filter[]
  operator?: string
  group?: Group<T>
  groupIndex?: number
  rule?: Rule<T>
  ruleIndex?: number
  comparison?: string
  extra?: T
}

export type State<T> = {
  isDropdownOpen: boolean
  isGroupDropdownOpen: boolean
  activeGroupIndex?: number
  activeRuleIndex?: number
  globalOperator: string
  groups: Group<T>[]
}

export type Reducer<T> = (state: State<T>, action: Action<T>) => State<T>

const filterReducer = {
  initialState: {
    isDropdownOpen: false,
    isGroupDropdownOpen: false,
    activeGroupIndex: -1,
    activeRuleIndex: -1,
    globalOperator: 'and',
    groups: [] as Group<any>[],
  },

  reducer<T>(state: State<T>, action: Action<T>): State<T> {
    switch (action.type) {
      case 'CLICK_ADD_GROUP':
        return {
          ...state,
          isDropdownOpen: true,
          activeGroupIndex: action.groupIndex,
          activeRuleIndex: action.ruleIndex,
        }
      case 'CLICK_ADD_RULE':
        return {
          ...state,
          isGroupDropdownOpen: true,
          activeGroupIndex: action.groupIndex,
          activeRuleIndex: action.ruleIndex,
        }
      case 'CLOSE_DROPDOWN':
        return { ...state, isDropdownOpen: false }
      case 'CLOSE_POPOVER':
        if (
          action.groupIndex !== state.activeGroupIndex ||
          action.ruleIndex !== state.activeRuleIndex
        ) {
          return state
        }
        return {
          ...state,
          activeGroupIndex: -1,
          activeRuleIndex: -1,
        }
      case 'OPEN_POPOVER':
        return {
          ...state,
          activeGroupIndex: action.groupIndex!,
          activeRuleIndex: action.ruleIndex!,
        }
      case 'ADD_RULE': {
        const { groupIndex, rule } = action

        // If a valid groupIndex is provided, add the rule to that group's rules array
        if (typeof groupIndex === 'number' && state.groups[groupIndex]) {
          const updatedGroups = state.groups.map((group, gIdx) =>
            gIdx === groupIndex ? { ...group, rules: [...(group.rules || []), rule] } : group
          )
          return { ...state, groups: updatedGroups, isDropdownOpen: false }
        }

        // If no valid groupIndex is provided, create a new group with this rule
        const newGroup = {
          operator: 'and',
          rules: [rule],
        }

        return {
          ...state,
          groups: [...state.groups, newGroup],
          isDropdownOpen: false,
        }
      }
      case 'REMOVE_RULE': {
        const modifiedGroups = state.groups.map((group, gIdx) =>
          gIdx === action.groupIndex
            ? {
                ...group,
                rules: (group.rules || []).filter((_, rIdx) => rIdx !== action.ruleIndex),
              }
            : group
        )
        const filteredGroups = modifiedGroups.filter((group) => (group.rules || []).length > 0)

        return { ...state, groups: filteredGroups }
      }
      case 'SET_FILTER':
      case 'SET_COMPARISON':
      case 'SET_EXTRA': {
        const currentRule = state.groups[action.groupIndex!].rules[action.ruleIndex!]
        const updatedRule = {
          ...currentRule,
          filters: action.filters || currentRule?.filters,
          comparison: action.comparison || currentRule?.comparison,
          extra: action.extra || currentRule?.extra,
        }

        const updatedRules = R.update(
          action.ruleIndex!,
          updatedRule,
          state.groups[action.groupIndex!].rules
        )

        const updatedGroupList = R.update(
          action.groupIndex!,
          { ...state.groups[action.groupIndex!], rules: updatedRules },
          state.groups
        )

        return { ...state, groups: updatedGroupList }
      }
      case 'SET_GLOBAL_OPERATOR': {
        const globalOperator = action.operator ?? 'and'
        return { ...state, globalOperator }
      }
      case 'SET_GROUP_OPERATOR': {
        const groups = state.groups.map((group, gIdx) =>
          gIdx === action.groupIndex ? { ...group, operator: action.operator ?? 'and' } : group
        )
        return { ...state, groups, isDropdownOpen: false }
      }
      default:
        return state
    }
  },

  createActions<T>(dispatch: (action: Action<T>) => void) {
    return {
      addRule(rule: Rule<T>, groupIndex?: number) {
        dispatch({ type: 'ADD_RULE', rule, groupIndex })
      },
      clickAddGroup(groupIndex: number, ruleIndex?: number) {
        dispatch({ type: 'CLICK_ADD_GROUP', groupIndex, ruleIndex })
      },
      clickAddRule(groupIndex: number, ruleIndex?: number) {
        dispatch({ type: 'CLICK_ADD_RULE', groupIndex, ruleIndex })
      },
      closeDropdown() {
        dispatch({ type: 'CLOSE_DROPDOWN' })
      },
      closePopover(groupIndex: number, ruleIndex: number) {
        dispatch({ type: 'CLOSE_POPOVER', groupIndex, ruleIndex })
      },
      openPopover(groupIndex?: number, ruleIndex?: number) {
        dispatch({ type: 'OPEN_POPOVER', groupIndex, ruleIndex })
      },
      removeRule(groupIndex: number, ruleIndex: number) {
        dispatch({ type: 'REMOVE_RULE', groupIndex, ruleIndex })
      },
      setComparison(groupIndex: number, ruleIndex: number, comparison: Comparison) {
        dispatch({ type: 'SET_COMPARISON', groupIndex, ruleIndex, comparison })
      },
      setExtra(groupIndex: number, ruleIndex?: number, extra?: T) {
        dispatch({ type: 'SET_EXTRA', groupIndex, ruleIndex, extra })
      },
      setFilter(groupIndex: number, ruleIndex: number, filters: Filter[]) {
        dispatch({ type: 'SET_FILTER', groupIndex, ruleIndex, filters })
      },
      setGlobalOperator(operator: string) {
        dispatch({ type: 'SET_GLOBAL_OPERATOR', operator })
      },
      setGroupOperator(groupIndex: number, operator) {
        dispatch({ type: 'SET_GROUP_OPERATOR', groupIndex, operator })
      },
    }
  },
}

export default filterReducer



// WEBPACK FOOTER //
// ./src/shared/components/FilterGroupV2/reducer.ts